<template>
  <div id="micro-table" class="microdata-table">
    <div class="header-container">
      <div v-if="microDataMetas && microDataMetas.code !== 500" class="table-mode-wrapper">
        <div class="table-mode-inner">
          <div
            class="table-mode-button"
            :class="tableMode === 'compact' ? 'active' : ''"
            @click="setTableMode('compact')"
          >
            <span class="mode-text">{{ $t('table_compact') }}</span>
          </div>
          <div
            class="table-mode-button"
            :class="
              (tableMode === 'detail' ? 'active' : '') + ' ' + (calculatedData ? '' : 'disabled')
            "
            @click="setTableMode('detail')"
          >
            <span class="mode-text">{{ $t('table_detail') }}</span>
          </div>
          <div
            class="table-mode-button"
            :class="
              (tableMode === 'column' ? 'active' : '') + ' ' + (calculatedData ? '' : 'disabled')
            "
            @click="setTableMode('column')"
          >
            <span class="mode-text">{{ $t('table_column') }}</span>
          </div>
        </div>
        <span v-if="!calculatedData && !reloadMicroDataMetas.uncalculable" class="table-mode-loader-text">{{
          $t('microdata_loader')
        }}</span>
      </div>
      <div v-if="sortedOptions" class="filter-container">
        <div v-if="tableMode !== 'column'" class="rows-wrapper">
          <div class="icon-eye" />
          <span>{{
            visibleRows + $t('rows_of') + (microDataMetas.count ? microDataMetas.count : 0)
          }}</span>
        </div>
        <div class="column-filter-select">
          <div class="selector-wrapper" @click="toggleFilterColumn">
            <span class="selector-text">{{
              sortedOptions.length -
                disabledColumnIds.length +
                $t('of') +
                sortedOptions.length +
                $t('num_cols')
            }}</span>
            <div class="icon-chev" :class="columnFilterOpen && 'open-menu'" />
          </div>
          <div
            id="col-micro-table"
            class="column-filter-dropdown"
            :class="!columnFilterOpen && 'hidden'"
          >
            <div class="all-option" @click="enableAllColumns">
              <span class="all-text">{{ $t('select_all') }}</span>
            </div>
            <div class="all-option" @click="disableAllColumns">
              <span class="all-text">{{ $t('unselect_all') }}</span>
            </div>
            <draggable v-model="sortedOptions" @start="drag = true" @end="drag = false">
              <transition-group type="transition" :name="!drag ? 'flip-list' : null">
                <div
                  v-for="col in sortedOptions"
                  :key="col.label"
                  class="option-wrapper"
                  :class="disabledColumnIds.indexOf(col.label) !== -1 && 'unselected'"
                  @click="toggleColumnViewState(col)"
                >
                  <div class="handle" />
                  <div class="option-container">
                    <div class="fake-checkbox" />
                    <span>{{ normalizeCsvHeader(col.label) }}</span>
                  </div>
                </div>
              </transition-group>
            </draggable>
          </div>
        </div>
        <div v-if="enableGlobalSearch" class="table-search">
          <div class="search-button-icon" @click="searchHandler" />
          <input
            id="tableInput"
            v-model="tableSearch"
            class="table-input-search"
            type="text"
            @change="searchHandler"
          >
        </div>
        <div class="reset-button" :class="!enableReset && 'hidden'" @click="resetCsv">
          <div class="reset-icon" />
        </div>
      </div>
    </div>
    <div class="table-over-wrapper">
      <div ref="tableWrapper" class="table-wrapper" :class="{ hidden: (tableMode === 'column' || sortedViewedCols && sortedViewedCols.length === 0) }">
        <table ref="microtable" />
      </div>
      <MicroStats
        v-if="tableMode === 'column'"
        :data="calculatedData.data"
        :total="calculatedData.count"
        :sorted-options="sortedViewedCols"
      />
      <div v-if="sortedViewedCols && sortedViewedCols.length === 0" class="no-data-info">
        {{ $t('no_data_for_these_filters') }}
      </div>
    </div>

    <Loader v-if="!microDataMetas" />
    <span v-else-if="microDataMetas.code === 500" class="error-feedback">
      {{ $t('backend_error') }}
    </span>
    <span v-else-if="microDataMetas.code === 404" class="error-feedback">
      {{ $t('not_csv') }}
    </span>
    <div ref="colDropdown" v-click-outside="onDropdownClickOutside" class="col-dropdown">
      <template v-if="activeConfigCol">
        <div
          class="row sort-option asc"
          :class="{ active: activeConfigCol.dir === 'asc' }"
          data-id="asc"
          @click="() => setTempConfigCol('asc')"
        >
          <div class="icon" />
          <div class="label">{{ $t('table_ord_asc') }}</div>
        </div>
        <div
          class="row sort-option desc"
          :class="{ active: activeConfigCol.dir === 'desc' }"
          data-id="desc"
          @click="() => setTempConfigCol('desc')"
        >
          <div class="icon" />
          <div class="label">{{ $t('table_ord_desc') }}</div>
        </div>
        <div
          class="row sort-option reset"
          :class="{ active: activeConfigCol.dir === 'reset' }"
          data-id="reset"
          @click="() => setTempConfigCol('reset')"
        >
          <div class="icon" />
          <div class="label">{{ $t('table_reset') }}</div>
        </div>
        <div v-if="activeConfigCol.filterType !== 'key' && enableColumnSearch" class="special-row">
          <ColumnFilter
            v-if="activeConfigCol.filterType !== 'range' || activeConfigCol.fullRange"
            v-model="activeConfigCol.filter"
            :type="activeConfigCol.filterType"
            :full-range="activeConfigCol.fullRange"
          />
        </div>
        <div class="row actions">
          <div class="action-button" @click="clearColConfig">{{ $t('btn_clean') }}</div>
          <div class="action-button main" @click="applyColConfig">{{ $t('btn_apply') }}</div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getMicrodataFileMetas, getMicrodataFileData } from '@/services/api'
import ColumnFilter from './ColumnFilter.vue'
import draggable from 'vuedraggable'
import Loader from '../Loader.vue'
import $ from 'jquery'
import ClickOutside from 'vue-click-outside'
import { flatNumberFormat } from '@/utils/formatUtils.js'
import { getMicrodataCellValue, normalizeCsvHeader } from '@/utils/microdataUtils.js'
import MicroChartCell from './MicroChartCell.vue'
import { t } from '@/i18n'

import Vue from 'vue'
import 'datatables.net-dt'
//import 'datatables.net-dt/css/jquery.dataTables.css'
import 'datatables.net-scroller-dt'
import 'datatables.net-scroller-dt/css/scroller.dataTables.css'
import MicroStats from './MicroStats.vue'

export default {
  directives: { ClickOutside },
  components: { ColumnFilter, draggable, Loader, MicroStats },
  props: {
    tableId: {
      type: String,
    },
  },
  data() {
    return {
      colsConfig: {
        currentSort: null,
        filters: {},
      },
      activeConfigCol: null,
      disabledColumnIds: [],
      columnFilterOpen: false,
      tableSearch: '',
      tableMode: 'compact',
      numberOfRows: 100,
      visibleRows: 0,
      sortedOptions: null,
      drag: false,
      enableReset: false,
      enableGlobalSearch: true,
      enableColumnSearch: true,
      reloadCsv: false,
      reloadData: false,
    }
  },
  computed: {
    sortedViewedCols() {
      return this.sortedOptions && this.sortedOptions.filter(el => this.disabledColumnIds.indexOf(el.label) === -1)
    },
    header() {
      return this.microDataMetas ? this.microDataMetas.data : null
    },
    colFilterTypes() {
      let result = this.header.map(el => {
        return el.type === 'text' ? 'string' : el.type === 'key' ? null : 'range'
      })
      return result
    },
    colRanges() {
      //[Infinity, -Infinity]
      let result = this.header.map(el => {
        if (typeof el.min !== 'undefined' && typeof el.max !== 'undefined') return [el.min, el.max]
        else return undefined
      })
      return result
    },
    sortingParseBackend() {
      if (this.colsConfig?.currentSort?.key && this.colsConfig?.currentSort?.dir)
        return this.colsConfig?.currentSort?.key + ' ' + this.colsConfig?.currentSort?.dir
      else return undefined
    },
    searchParseBackend() {
      let arrayOfSearch = []
      let self = this
      if (this.tableSearch !== '') arrayOfSearch.push({ column: '', text: this.tableSearch })
      if (self.activeConfigCol) {
        if (self.colsConfig.filters && Object.keys(self.colsConfig.filters).length !== 0)
          Object.keys(self.colsConfig.filters).forEach(el => {
            if (typeof self.colsConfig.filters[el] == 'string')
              arrayOfSearch.push({
                column: self.sortedOptions[parseInt(el)].label,
                text: self.colsConfig.filters[el],
              })
          })
      }
      return arrayOfSearch
    },
    calculatedData() {
      return this.microDataMetas?.calculated ? this.microDataMetas : null
    },
  },
  watch: {
    header() {
      this.sortedOptions = this.header
    },
    sortedOptions() {
      this._DataTable_?.draw()
      if (this.sortedOptions !== this.header) this.enableReset = true
      this.updateDataTable()
      this.disabledColumnIds.forEach(col => {
        let columna = this._DataTable_.column(this.sortedOptions.findIndex(el => el.label === col))
        // Toggle the visibility
        columna.visible(false)
      })
    },
    reloadMicroDataMetas() {
      if (this.reloadMicroDataMetas && !this.reloadMicroDataMetas.calculated && !this.reloadMicroDataMetas.uncalculable) {
        setTimeout(() => {
          this.reloadData = !this.reloadData
        }, 20000)
      } else if (this.microDataMetas && !this.microDataMetas.calculated && !this.reloadMicroDataMetas.uncalculable) {
        this.reloadCsv = !this.reloadCsv
      }
    },
  },
  mounted() {
    this.updateDataTable()
  },
  asyncComputed: {
    microDataMetas() {
      this.reloadCsv
      return getMicrodataFileMetas(this.tableId)
    },
    reloadMicroDataMetas() {
      this.reloadData
      return getMicrodataFileMetas(this.tableId)
    },
  },
  methods: {
    enableAllColumns() {
      this.disabledColumnIds = []
      this._DataTable_.columns().visible(true)
    },
    disableAllColumns() {
      this.disabledColumnIds = this.sortedOptions.map(el => el.label)
      this._DataTable_.columns().visible(false)
    },
    clearColConfig() {
      this.activeConfigCol.filter = null
    },
    searchHandler() {
      this.updateDataTable()
    },
    resetCsv() {
      this.colsConfig = {
        currentSort: null,
        filters: {},
      }
      this.activeConfigCol = null
      this.disabledColumnIds = []
      this.tableSearch = ''
      this.numberOfRows = 100
      this.sortedOptions = this.header
      this.updateDataTable()
      this.enableReset = false
    },
    setTableMode(mode) {
      this.tableMode = mode
      this.disabledColumnIds = []
      this.updateDataTable()
    },
    toggleColumnViewState(column) {
      this.enableReset = true
      if (this.disabledColumnIds.indexOf(column.label) !== -1) {
        this.disabledColumnIds = this.disabledColumnIds.filter(e => e != column.label)
      } else {
        this.disabledColumnIds.push(column.label)
      }
      let columna = this._DataTable_.column(this.sortedOptions.indexOf(column))
      // Toggle the visibility
      columna.visible(!columna.visible())
    },
    toggleFilterColumn() {
      this.columnFilterOpen = !this.columnFilterOpen
      if (this.activeConfigCol) this.activeConfigCol = null
    },
    setTempConfigCol(dir) {
      this.activeConfigCol.dir = dir
      this.showColDropdown()
    },
    applyColConfig() {
      if (this.activeConfigCol.dir && this.activeConfigCol.dir !== 'reset') {
        this.enableReset = true
        this.colsConfig.currentSort = {
          index: this.activeConfigCol.index,
          key: this.activeConfigCol.key,
          dir: this.activeConfigCol.dir,
        }
        this._DataTable_.order([this.colsConfig.currentSort.index, this.colsConfig.currentSort.dir])
      } else if (this.activeConfigCol.dir == 'reset') {
        this.colsConfig.currentSort = {}
        // NO ESTA NADA CLARO QUE ESTO SEA "el orden original" == "el orden del configGroup rows"
        this._DataTable_.order([0, 'asc'])
      }
      if (!this.activeConfigCol.filter) delete this.colsConfig.filters[this.activeConfigCol.index]
      else this.colsConfig.filters[this.activeConfigCol.index] = this.activeConfigCol.filter

      this.hideColDropdown()
      this.updateDataTable()
    },
    onDropdownClickOutside() {
      if (this.colsConfig) this.hideColDropdown()
    },
    hideColDropdown() {
      $(this.$refs.colDropdown).removeClass('visible')

      let $header = $(this.$el).find('.dataTables_scrollHead')
      $header
        .find('tr')
        .last()
        .find('th')
        .each((i, el) => {
          let $icon = $(el).find('.filter-sort-icon')
          $icon.removeClass('icon-visible')
        })
    },
    showColDropdown() {
      $(this.$refs.colDropdown).addClass('visible')
      let self = this
      setTimeout(() => {
        let pos = self.$refs.colDropdown.getBoundingClientRect()
        let table = document.getElementsByClassName('table-wrapper')[0].getBoundingClientRect()
        if (table.right < pos.right) {
          self.$refs.colDropdown.style.right = '-11px'
          self.$refs.colDropdown.style.left = ''
        }
      }, 1)
      if (this.columnFilterOpen) this.columnFilterOpen = false
    },
    normalizeCsvHeader(label) {
      return normalizeCsvHeader(label)
    },
    getMicrodataCellValue(row, column, columns) {
      return getMicrodataCellValue(row, column, columns)
    },
    formatMicrodataCellValue(row, column, columns) {
      let value = this.getMicrodataCellValue(row, column, columns)
      if (value === null || value === undefined || value === '') return '-'
      if (column.type === 'num') return flatNumberFormat(value)

      let textValue = String(value)
      return textValue.length > 150 ? textValue.substring(0, 150) + '...' : textValue
    },
    updateDataTable() {
      if (this.sortedOptions) {
        let self = this
        let $table = $(this.$refs.microtable)
        if (this._DataTable_) {
          $table.off('draw.dt')
          this._DataTable_.clear()
          this._DataTable_.destroy()
          $table.empty()
        }
        let dropdownParent = $(this.$refs.tableWrapper)
        let dropdown = $(this.$refs.colDropdown)
        let $header = $('<thead></thead>')
        let $tr = $('<tr></tr>').appendTo($header)
        // let $chartTr = $('<tr></tr>').appendTo($header)
        this.sortedOptions.forEach((hr, j) => {
          $tr.append(`<th style="min-width: 100px; max-width: 100px;">
            <div class="header-container" style="display: flex; width: 100%; justify-content: flex-end;">
              <span>${this.normalizeCsvHeader(hr.label)}</span>
              <div data-index="${j}" class="sort-and-filter"><div class="filter-sort-icon" />
            </div>
          </th>
          `)
          $tr.find('.sort-and-filter').on('click', function (e) {
            setTimeout(() => {
              let colIndex = +e.currentTarget.getAttribute('data-index')
              let colKey = self.sortedOptions[colIndex].label
              let currSort =
                self.colsConfig?.currentSort?.index === colIndex && self.colsConfig.currentSort.dir
              let currFilter =
                (self.colsConfig?.filters && self.colsConfig?.filters[colIndex]) || null
              self.activeConfigCol = {
                index: colIndex,
                key: colKey,
                dir: currSort,
                filter: currFilter,
                filterType: self.colFilterTypes[colIndex],
                fullRange: self.colRanges[colIndex],
              }
              if (e.currentTarget.children[0].className.indexOf('icon-visible' === -1))
                e.currentTarget.children[0].className = 'filter-sort-icon icon-visible'

              self.showColDropdown()
              let offset = $header.closest('.table-wrapper')[0].getBoundingClientRect()
              let rect = e.currentTarget.parentElement.parentElement.getBoundingClientRect()

              dropdown.css({
                left: rect.left - offset.left + 10,
                top: rect.top - offset.top + rect.height - 10,
              })
              dropdownParent.append(dropdown)
            }, 1)
          })
        })
        if (this.tableMode === 'detail') {
          this.sortedOptions.forEach((hr, j) => {
            var ComponentClass = Vue.extend(MicroChartCell)
            let colData = this.calculatedData.data[j]
            let instance = new ComponentClass({
              propsData: { data: colData, fixedTooltip: true },
            })
            instance.$mount()
            $tr.children()[j].append(instance.$el)
          })
        }
        $table.append($header)
        //ENABLE OR DISABLE SEARCH FUNCTIOALITIES
        if (
          this.searchParseBackend.length === 0 &&
          Object.keys(this.colsConfig.filters).length === 0
        ) {
          //enable all text filter
          this.enableGlobalSearch = true
          this.enableColumnSearch = true
        } else if (this.searchParseBackend.find(el => el.column === '') !== undefined) {
          //disable column filter
          this.enableGlobalSearch = true
          this.enableColumnSearch = false
          this.enableReset = true
        } else if (Object.keys(this.colsConfig.filters).length !== 0) {
          this.enableReset = true
          let hasTextFilter = false
          Object.keys(this.colsConfig.filters).forEach(el => {
            if (this.colsConfig.filters[el] !== null) hasTextFilter = true
          })
          if (hasTextFilter) {
            //disable global text filter
            this.enableGlobalSearch = false
            this.enableColumnSearch = true
          } else {
            this.enableGlobalSearch = true
            this.enableColumnSearch = true
          }
        }
        //parsingFilters
        let filter = this.colsConfig.filters
        let keyFil = Object.keys(filter)
        let fils = keyFil.filter(el => typeof filter[el] !== 'string')
        let parsedFils = []
        fils.forEach(el => {
          parsedFils.push({
            column: this.sortedOptions[parseInt(el)].label,
            value: filter[el].join(':'),
          })
        })
        this._DataTable_ = $table.DataTable({
          searching: false,
          //paging: false,
          //pagingType: 'scrolling',
          //sPaginationType: 'scrolling',

          // processing: true,
          serverSide: true,
          ajax: (data, callback) =>
            //offset = data.start
            getMicrodataFileData(
              this.tableId,
              this.numberOfRows,
              //data.length,
              data.start,
              this.searchParseBackend,
              this.sortingParseBackend,
              parsedFils
            )
              .then(json => {
                let orderedData = []
                let formatedData = []
                json.data?.forEach(el => {
                  self.sortedOptions.forEach(key => {
                    formatedData.push(self.formatMicrodataCellValue(el, key, self.sortedOptions))
                  })
                  orderedData.push(formatedData)
                  formatedData = []
                })
                this.visibleRows = json.count ? json.count : 0
                return { data: orderedData, count: json.count }
              })
              .then(data => {
                return callback({
                  data: data.data,
                  recordsTotal: data.count,
                  recordsFiltered: data.count,
                })
              }),
          ordering: false,
          //pagingType: 'simple_numbers',
          deferRender: true,
          scrollY: 350, //- this.computedHeaderHeight - this.computedFooterHeight, //(parece que "fixedHeader" + "Scrolly" no lo sabe hacer...)
          scrollCollapse: true,
          scrollX: true,
          info: false,
          autoWidth: false,
          scroller: {
            loadingIndicator: true,
          },
          language: {
            emptyTable: t('no_data_for_these_filters'),
          },
        })
        this.disabledColumnIds.forEach(col => {
          let columna = this._DataTable_.column(this.sortedOptions.findIndex(el => el.label === col))
          // Toggle the visibility
          columna.visible(false)
        })

        $table.on('mouseenter', 'td', function () {
          var colIdx = self._DataTable_.cell(this).index()?.columnVisible
            ? self._DataTable_.cell(this).index()?.columnVisible
            : self._DataTable_.cell(this).index()?.column

          $(self._DataTable_.cells().nodes()).removeClass('highlight')
          $(self._DataTable_.column(colIdx).nodes()).addClass('highlight')
          let header = $(self.$el).find('.dataTables_scrollHead')
          header
            .find('tr')
            .last()
            .find('th')
            .each((i, el) => {
              if (i === colIdx) {
                el.classList.add('highlight')
              } else el.classList.remove('highlight')
            })
        })

        $table.closest('.dataTables_scrollBody').on('scroll', () => {
          this.onDropdownClickOutside()
        })

        $table.on('draw.dt', () => {
          console.log('should update header styles')
          let $header = $(this.$el).find('.dataTables_scrollHead')
          $header
            .find('tr')
            .last()
            .find('th')
            .each((i, el) => {
              let $icon = $(el).find('.sort-and-filter')
              $icon.removeClass('filtering').removeClass('sorting-asc').removeClass('sorting-desc')
              if (
                self.colsConfig.currentSort &&
                self.normalizeCsvHeader(self.colsConfig.currentSort.key) ===
                  self.normalizeCsvHeader(el.innerText)
              ) {
                $icon.addClass('sorting-' + self.colsConfig.currentSort.dir)
              }
              // let colId = $icon.attr('data-id')
              if (self.colsConfig.filters && self.colsConfig.filters[i]) {
                $icon.addClass('filtering')
              }
            })
        })
      }
    },
  },
}
</script>

<style lang="stylus" scoped>
$group-border-separation = 8px
m-fake-right-border($color = #000)

.microdata-table
  width: 100%
  
  .error-feedback
    padding: vw(15px) vw(20px)
    line-height: vw(70px)

  .header-container
    align-items: center
    display: flex
    gap: vw(5px)
    padding: vw(30px) vw(20px)

    .desktop &
      justify-content: space-between

    .mobile &
      flex-direction: column

    .filter-container
      align-items: center
      display: flex
      flex-direction: row
      flex-wrap: wrap
      gap: vw(6px)

      .mobile &
        width: 100%

      .column-filter-select
        padding: 0 vw(12px)
        position: relative
        width: max-content

        .mobile &
          margin-left: auto
          padding-right: 0

        .selector-wrapper
          align-items: center
          display: flex
          gap: vw(4px)

          .selector-text
            m-font-size(16, 19)
            color: $corporate_color
            text-decoration: underline

          .icon-chev
            m-icon("chevron-up-blue", 26)
            animation: 0.3s
            cursor: pointer
            transform: rotate(180deg)

          .open-menu
            animation-name: rotate
            transform: rotate(0)

          @keyframes rotate
            from
              transform: rotate(180deg)

            to
              transform: rotate(0)

      .column-filter-dropdown
        background-color: white
        border: 1px solid $bcn-other-grey
        border-radius: vw(8px)
        display: block
        max-height: vw(400px)
        min-width: vw(300px)
        opacity: 1
        overflow-y: scroll
        padding: vw(10px) vw(20px)
        position: absolute
        right: 0
        top: vw(32px)
        z-index: 3

        &.hidden
          opacity: 0
          z-index: 0

        .actions
          display: inline-flex
          gap: vw(5px)
          margin-top: vw(14px)
          width: 100%

          .action-button
            align-items: center
            border: solid 1px
            cursor: pointer
            display: flex
            justify-content: center
            padding: vw(8px)
            width: 100%

            &.main
              background: #000
              color: #fff

        .all-option
          cursor: pointer
          padding: vw(4px) vw(4px) vw(4px) vw(15px)

          &:hover
            background-color: $bcn-very-light-grey

          .all-text
            m-font-size(16, 20)
            font-weight: 400

        .option-wrapper
          align-items: center
          display: flex

          .fake-checkbox
            m-icon("check-box", 20, $corporate_color)
            margin-right: vw(10px)
            margin-top: vw(1px)

          .handle
            m-icon("drag", 20)
            cursor: grab

          .option-container
            align-items: center
            cursor: pointer
            display: flex
            padding: vw(4px)
            width: 100%

            &:hover
              background-color: $bcn-very-light-grey

          span
            m-font-size(16, 20)
            color: $corporate_color
            font-weight: 400

          &.unselected
            .fake-checkbox
              m-icon("check-box-blank", 20, #000)

            span
              color: $grey-dark

      .table-search
        position: relative

        .mobile &
          flex: 1 1 100%

        .search-button-icon
          left: vw(3px)
          position: absolute
          m-icon("search", 26)
          top: vw(6px)

        .table-input-search
          m-font-size(16, 20)
          background-color: $bcn-grey
          border: none
          border-radius: 5px
          padding: vw(8px) vw(8px) vw(8px) vw(35px)

          .mobile &
            width: 100%

      .rows-wrapper
        align-items: center
        display: flex
        gap: vw(6px)
        width: max-content

        .mobile &
          flex: 1 1 0%

        .icon-eye
          m-icon("eye", 20)

        span
          color: $grey
          m-font-size(15, 19)

      .reset-button
        align-items: center
        background-color: white
        border: 1px solid black
        border-radius: 50%
        cursor: pointer
        display: flex
        height: vw(32px)
        justify-content: center
        width: vw(32px)

        &:hover
          background-color: $bcn-table-highlight

        &.hidden
          display: none

        .reset-icon
          m-icon("refresh", 18)

    .table-mode-wrapper
      display: flex

      .desktop &
        align-items: center
        gap: vw(20px)

      .mobile &
        flex-direction: column
        margin-bottom: vw(20px)
        width: 100%

    .table-mode-inner
      align-items: center
      display: flex
      gap: vw(10px)

      .mobile &
        margin-bottom: vw(10px)
        width: 100%

    .table-mode-loader-text
      m-font-size(12, 16)

      .desktop &
        margin-top: 0.35em

    .table-mode-button
      m-font-size(16, 25)
      background-color: #fff
      border: 1px solid #fff
      border-radius: 4px
      cursor: pointer
      padding: vw(8px) vw(20px)

      .mobile &
        flex: 1 1 0%
        text-align: center

      &:hover
        background-color: $corpotative-color-very-light
        border-color: $lighter-blue

      &.active
        background-color: $lighter-blue
        border-color: $corporate_color
        color: $corporate_color

      &.disabled
        background-color: $bcn-very-light-grey
        border: 1px solid $grey-grey
        cursor: not-allowed
        pointer-events: none

        span
          color: $grey-grey

  .col-dropdown
    background: #fff
    border: solid 1px #ddd
    border-radius: vw(7px)
    // height 200px
    box-shadow: 5px 5px 7px rgba(0, 0, 0, 0.05)
    display: none
    flex-direction: column
    gap: vw(5px)
    padding: vw(8px) vw(6px) 0
    position: absolute
    transform: translate(-10px, 10px)
    width: vw(250px)
    z-index: 4

    &.visible
      display: flex

    .special-row
      margin-top: vw(8px)

    .sort-option
      align-items: center
      cursor: pointer
      display: flex

      &.active
        font-weight: bold

      .icon
        margin: vw(4px)

      &.asc
        .icon
          m-icon("arrow-back", 18)
          transform: rotate(90deg)

      &.desc
        .icon
          m-icon("arrow-back", 18)
          transform: rotate(-90deg)

      &.reset
        .icon
          m-icon("refresh", 18)

    .actions
      display: inline-flex
      gap: vw(5px)
      margin: 0
      margin-top: vw(10px)
      padding: vw(10px)
      width: 100%

      .action-button
        align-items: center
        border: solid 1px
        cursor: pointer
        display: flex
        justify-content: center
        padding: vw(8px)
        width: 100%

        &.main
          background: #000
          color: #fff

  .table-over-wrapper
    position: relative
    
  .no-data-info
    padding: 0 vw(10px)
    m-font-size(17, 19)
    text-align: center
    margin-top: vw(20px)

  .table-wrapper
    position: relative

    >>> .dataTables_empty
      text-align: center
      m-font-size(14, 16)

    &.hidden
      display: none

    >>> table
      m-font-size(14, 16)
      border-collapse: collapse
      border-spacing: 0

      tbody
        tr
          &.odd
            td
              background-color: #ffffff

          &.even
            td
              background-color: $bcn-table-even

          &:hover
            td
              &.highlight
                background-color: $bcn-table-highlight // color de elemento highlithed

            td:first-child
              background-color: $corpotative-color-very-light // color de primer elemento fila highlithed
              border: none
              box-shadow: 0 0 0 1px $corporate_color_light inset

          td
            border-right: solid 1px #ddd
            box-sizing: content-box
            font-weight: 400
            max-width: 100px !important
            min-width: 100px !important
            padding: vw(10px)
            text-align: right
            white-space: pre-wrap
            width: 100px !important
            word-wrap: break-word
            // display: -webkit-box
            // -webkit-line-clamp: 5
            // -webkit-box-orient: vertical
            // overflow: hidden

      thead
        tr
          th
            background: $bcn-table-even
            background-image: none !important
            border: solid 1px #ddd
            // border: none
            font-weight: 600
            padding: 10px 10px 10px vw(45px) // delicado, hay unos paddings de la "dataTalbe" peor queremos dejar MAS espacio a la derecha para los "sort-and-filter"
            position: relative

            &.highlight
              background-color: #f8f8ff // color de columna highlithed
              box-shadow: 0 0 0 1px $corporate_color_light inset

            &:hover
              background: $bcn-table-even

            .header-container
              align-items: center
              min-height: vw(20px)

            .micro-chart-cell
              border-top: solid 1px #ddd
              height: vw(80px)
              margin-left: vw(-45px)
              margin-top: 10px
              width: "calc(100% + %s)" % vw(55px)

            .sort-and-filter
              cursor: pointer
              display: flex
              opacity: 1
              transition: opacity 0.3s

              &.sorting-asc
                &:before
                  content: " "
                  display: flex
                  height: vw(18px)
                  width: vw(18px)
                  m-icon("order-up-blue", 18)

              &.sorting-desc
                &:before
                  m-icon("order-up-blue", 18)
                  content: " "
                  display: flex
                  height: vw(20px)
                  transform: rotate(180deg)
                  width: vw(20px)

              &.filtering
                .filter-sort-icon
                  display: flex
                  m-icon("sort-blue", 20)

              .filter-sort-icon
                display: none
                m-icon("sort", 20)

                &.icon-visible
                  display: flex

            &:hover
              .sort-and-filter
                opacity: 1

                .filter-sort-icon
                  display: flex
</style>
